import {
 signInWithEmailAndPassword,
 onAuthStateChanged,
 signOut as fbSignOut,
 RecaptchaVerifier,
 signInWithPhoneNumber,
 EmailAuthProvider,
 linkWithCredential,
 sendPasswordResetEmail,
 verifyPasswordResetCode,
 confirmPasswordReset,
 createUserWithEmailAndPassword,
 setPersistence,
 browserLocalPersistence,
 sendEmailVerification,
 updatePassword,
 reauthenticateWithCredential,
 applyActionCode,
 checkActionCode,
 PhoneAuthProvider,
 updatePhoneNumber,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { getAuthAsync } from "configuration";
import {
 SIGN_IN_ERROR_MESSAGES,
 SIGN_UP_ERROR_MESSAGES,
 FORGOT_PASSWORD_ERROR_MESSAGES,
 VERIFY_RESET_CODE_ERROR_MESSAGES,
 RESET_PASSWORD_ERROR_MESSAGES,
 PHONE_VERIFICATION_ERROR_MESSAGES,
 EMAIL_VERIFICATION_ERROR_MESSAGES,
 getErrorMessage,
} from "constants";

/* ------------------------ helpers ------------------------ */

const clearFirebaseAuthData = () => {
 try {
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
   const key = localStorage.key(i);
   if (key && (key.startsWith("firebase:") || key.startsWith("authUser"))) {
    keysToRemove.push(key);
   }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));

  const sessionKeysToRemove = [];
  for (let i = 0; i < sessionStorage.length; i++) {
   const key = sessionStorage.key(i);
   if (key && (key.startsWith("firebase:") || key.startsWith("authUser"))) {
    sessionKeysToRemove.push(key);
   }
  }
  sessionKeysToRemove.forEach((key) => sessionStorage.removeItem(key));

  // Also clear mobile session tokens
  localStorage.removeItem("mobile_session_token");
  localStorage.removeItem("mobile_session_uid");
 } catch {}
};

// Lazy helpers — evaluated at call time, not at module load.
// Avoids frozen values if navigator is not yet available at import time.
const getIsSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
const getIsMobile = () => {
 const ua = navigator.userAgent || navigator.vendor || window.opera;
 return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua.toLowerCase())
  || getIsSafari();
};

const getBaseUrl = () =>
 process.env.REACT_APP_AUTH_URL || window.location.origin;

/** Single landing page for *all* Firebase action links */
const buildActionCodeSettings = (kind, nextUrl) => {
 const base = getBaseUrl();
 const landing = new URL("/auth-action", base);
 if (nextUrl) landing.searchParams.set("returnUrl", nextUrl);

 const acs = {
  url: landing.toString(),
  handleCodeInApp: true,
 };

 if (process.env.REACT_APP_DYNAMIC_LINK_DOMAIN) {
  acs.dynamicLinkDomain = process.env.REACT_APP_DYNAMIC_LINK_DOMAIN;
 }
 return acs;
};

const cleanCode = (code) =>
 String(code ?? "")
  .replace(/['"]/g, "")
  .trim();

// Enhanced error wrapper
const createAuthError = (error, errorMap) => {
 const message = getErrorMessage(error, errorMap);
 const authError = new Error(message);
 authError.code = error?.code;
 authError.originalError = error;
 return authError;
};

/* ------------------------ service ------------------------ */

const createAuthenticationService = () => {
 let auth = null;
 let initialized = false;
 let initPromise = null;
 let recaptchaVerifier = null;
 let serverSessionCheckPromise = null;

 // Mobile-specific properties — evaluated here inside the factory so navigator is
 // guaranteed to be available. Treat Safari as mobile due to restrictive cookie policies.
 const isSafariBrowser = getIsSafari();
 const isMobile = getIsMobile();
 let mobileSessionToken = null;
   const OPAQUE_CODE_AUTH = process.env.REACT_APP_OPAQUE_CODE_AUTH === "true";

 // Set to true while signInWithEmail is in progress so the onAuthStateChanged
 // listener doesn't fire a competing createServerSession call and clobber
 // _pendingCode before signIn() reads it for the redirect.
 let _signingIn = false;

 const service = {
  /* ---------- init / session ---------- */

  // Exposed for UnprotectedPages to poll — lets it wait for
  // signInWithEmail to complete before reading _pendingCode.
  get _signingIn() {
   return _signingIn;
  },

  async initialize() {
   if (initialized) return auth;
   if (initPromise) return initPromise;

   initPromise = (async () => {
    auth = await getAuthAsync();
    await setPersistence(auth, browserLocalPersistence);
    initialized = true;

    // Load mobile session token if available
    if (isMobile) {
     try {
      mobileSessionToken = localStorage.getItem("mobile_session_token");
      if (mobileSessionToken) {
      }
     } catch (e) {
      console.error("Failed to load mobile session token:", e);
     }
    }

    return auth;
   })();

   return initPromise;
  },

  async checkServerSession() {
   if (serverSessionCheckPromise) return serverSessionCheckPromise;

   serverSessionCheckPromise = (async () => {
    try {
     // Build headers - include auth token for mobile
     const headers = {
      "Cache-Control": "no-store",
     };

     // Mobile: Include session token in Authorization header
     if (isMobile && mobileSessionToken) {
      headers["Authorization"] = `Bearer ${mobileSessionToken}`;
     }

     const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/status`, {
      credentials: "include",
      cache: "no-store",
      headers,
     });

     if (!res.ok) throw new Error(`Server responded with ${res.status}`);
     const data = await res.json();

     // If mobile and not authenticated, try with Firebase token
     if (isMobile && !data.authenticated && auth?.currentUser) {
      const idToken = await auth.currentUser.getIdToken();

      const retryRes = await fetch(
       `${process.env.REACT_APP_API_URL}/auth/status`,
       {
        credentials: "include",
        cache: "no-store",
        headers: {
         "Cache-Control": "no-store",
         Authorization: `Bearer ${idToken}`,
        },
       },
      );

      if (retryRes.ok) {
       return await retryRes.json();
      }
     }

     return data;
    } catch (error) {
     console.error("Server session check failed:", error);
     return { authenticated: false };
    } finally {
     serverSessionCheckPromise = null;
    }
   })();

   return serverSessionCheckPromise;
  },

  async getCurrentUser() {
   await service.initialize();

   // Return immediately if user is already available
   if (auth.currentUser) {
    return auth.currentUser;
   }

   // Wait for Firebase to restore auth state from persistence.
   // On Safari, ITP throttles localStorage access so restoration can
   // take longer than 1 second. We use a longer timeout on Safari/mobile
   // to avoid resolving null before Firebase has finished reading.
   const timeout = isMobile ? 5000 : 1000;

   return new Promise((resolve) => {
    let resolved = false;

    const unsubscribe = onAuthStateChanged(auth, (user) => {
     if (!resolved) {
      resolved = true;
      unsubscribe();
      resolve(user);
     }
    });

    setTimeout(() => {
     if (!resolved) {
      resolved = true;
      unsubscribe();
      resolve(null);
     }
    }, timeout);
   });
  },

  async createServerSession(user) {
   const idToken = await user.getIdToken(true);

   const response = await fetch(
    `${process.env.REACT_APP_API_URL}/auth/sign-in`,
    {
     method: "POST",
     headers: {
      "Content-Type": "application/json",
      // Include user agent for mobile detection on server
      "X-User-Agent": navigator.userAgent,
     },
     credentials: "include",
     body: JSON.stringify({ idToken }),
    },
   );

   if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || "Failed to authenticate with server");
   }

   const data = await response.json();

   // Flag ON: server returns a short-lived opaque code instead of the
   // raw session token. Store it for the redirect — the destination app
   // will exchange it for a real session cookie.
   if (isMobile && OPAQUE_CODE_AUTH) {
    if (data.code) {
     // Store temporarily for the redirect builder to read.
     // Never stored in localStorage — only lives in memory until redirect.
     // Only overwrite if there is no pending code already — prevents a
     // concurrent createServerSession call (e.g. from onAuthStateChanged)
     // from clobbering the code that signInWithEmail just stored.
     if (!service._pendingCode) {
      service._pendingCode = data.code;
      console.log('[auth] _pendingCode set:', data.code.slice(0,8) + '...');
     } else {
      console.log('[auth] _pendingCode already set, not overwriting');
     }
    } else {
     console.warn("[auth] OPAQUE_CODE_AUTH on but server returned no code");
    }
   } else if (isMobile && data.sessionToken) {
    // Flag OFF legacy path
    mobileSessionToken = data.sessionToken;
    try {
     localStorage.setItem("mobile_session_token", data.sessionToken);
     localStorage.setItem("mobile_session_uid", user.uid);
    } catch (e) {
     console.error("Failed to store mobile session:", e);
    }
   } else if (isMobile && !OPAQUE_CODE_AUTH && !data.sessionToken) {
    console.warn("[auth] Detected as mobile but backend returned no sessionToken");
   }

   return data;
  },

  // Exchange a short-lived opaque code for a server session cookie.
  // Called by destination apps (food-deal-ui, food-deal-vendor-ui) when
  // they land with ?code= in the URL.
  async exchangeCodeForSession(code) {
   const res = await fetch(
    `${process.env.REACT_APP_API_URL}/auth/exchange-code`,
    {
     method: "POST",
     headers: { "Content-Type": "application/json" },
     credentials: "include",
     body: JSON.stringify({ code }),
    },
   );
   if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Code exchange failed");
   }
   return res.json();
  },

  async onAuthStateChanged(callback) {
   await service.initialize();
   return onAuthStateChanged(auth, callback);
  },

  async syncServerSession(user, { forRedirect = false } = {}) {
   if (!user) return false;
   // If a sign-in is actively in progress, skip the concurrent sync —
   // signInWithEmail will handle session creation and the redirect.
   if (_signingIn) return true;

   try {
    const serverSession = await service.checkServerSession();

    // If not authenticated or UID mismatch, create new session.
    // On Safari/mobile with no redirect pending, skip createServerSession
    // entirely — the cookie will be dropped cross-origin by ITP and the
    // _pendingCode would go unused. The ID token Bearer fallback in
    // checkServerSession() is sufficient to authenticate on this domain.
    if (!serverSession.authenticated || serverSession.uid !== user.uid) {
     if (isMobile && OPAQUE_CODE_AUTH && !forRedirect) {
      // Safari: already authenticated via Bearer ID token fallback above.
      // No cookie needed on this domain. Return true so callers don't retry.
      return true;
     }
     await service.createServerSession(user);

     // DISABLED: This was overwriting the good session cookie from createServerSession
     // The sign-in endpoint already returns the correct session cookie, so we don't need this
     // Mobile: Additional sync endpoint for better reliability
     // if (isMobile) {
     //   const idToken = await user.getIdToken();
     //
     //   try {
     //     const mobileRes = await fetch(
     //       `${process.env.REACT_APP_API_URL}/auth/mobile-session`,
     //       {
     //         method: "POST",
     //         headers: {
     //           "Content-Type": "application/json",
     //           Authorization: `Bearer ${idToken}`,
     //         },
     //         credentials: "include",
     //         body: JSON.stringify({
     //           uid: user.uid,
     //           email: user.email,
     //         }),
     //       },
     //     );
     //
     //     if (mobileRes.ok) {
     //       const mobileData = await mobileRes.json();
     //       if (mobileData.sessionCookie) {
     //         mobileSessionToken = mobileData.sessionCookie;
     //         localStorage.setItem("mobile_session_token", mobileData.sessionCookie);
     //       }
     //     }
     //   } catch (e) {
     //     console.error("Mobile session sync failed:", e);
     //   }
     // }
    }

    return true;
   } catch (error) {
    console.error("Session sync failed:", error);
    return false;
   }
  },

  async getAuthenticationState() {
   const firebaseUser = await service.getCurrentUser();
   const serverSession = await service.checkServerSession();
   return {
    firebaseUser,
    serverSession,
    isFullyAuthenticated:
     !!firebaseUser &&
     !!serverSession.authenticated &&
     serverSession.uid === firebaseUser.uid,
    isMobile,
    isSafari: isSafariBrowser,
   };
  },

  /* ---------- sign in/out ---------- */

  async signInWithEmail(email, password) {
   await service.initialize();

   try {
    _signingIn = true;
    const userCred = await signInWithEmailAndPassword(auth, email, password);
    try {
     await service.createServerSession(userCred.user);
    } catch (error) {
     console.error("Failed to create server session:", error);
    }

    return userCred.user;
   } catch (error) {
    throw createAuthError(error, SIGN_IN_ERROR_MESSAGES);
   } finally {
    _signingIn = false;
   }
  },

  async signOut() {
   try {
    // Build headers for mobile
    const headers = {
     "Content-Type": "application/json",
    };

    if (isMobile && mobileSessionToken) {
     headers["Authorization"] = `Bearer ${mobileSessionToken}`;
    }

    // Sign out from server
    await fetch(`${process.env.REACT_APP_API_URL}/auth/sign-out`, {
     method: "POST",
     credentials: "include",
     headers,
    }).catch(() => {});

    // Clear mobile session
    if (isMobile) {
     mobileSessionToken = null;
     localStorage.removeItem("mobile_session_token");
     localStorage.removeItem("mobile_session_uid");
    }

    // Sign out from Firebase
    if (auth) await fbSignOut(auth);

    // Clear all auth data
    clearFirebaseAuthData();
   } catch {
    try {
     if (auth) await fbSignOut(auth);
     clearFirebaseAuthData();
    } catch {}
   }
  },

  /* ---------- Helper for authenticated API calls ---------- */

  async makeAuthenticatedRequest(url, options = {}) {
   const headers = {
    "Content-Type": "application/json",
    ...(options.headers || {}),
   };

   // Always include the Firebase ID token as a fallback Authorization header.
   // This covers the race condition where the session cookie from /auth/sign-in
   // hasn't been committed to the browser's cookie jar yet by the time the next
   // request fires (e.g. saveUserProfile immediately after signUp).
   // The requireSession middleware accepts both cookies and Bearer tokens,
   // so this works on all browsers — not just mobile.
   if (auth?.currentUser) {
    const idToken = await auth.currentUser.getIdToken();
    headers["Authorization"] = `Bearer ${idToken}`;
   } else if (isMobile) {
    // Mobile fallback: use stored session token if no Firebase user yet
    const token =
     mobileSessionToken || localStorage.getItem("mobile_session_token");
    if (token) {
     headers["Authorization"] = `Bearer ${token}`;
    }
   }

   return fetch(url, {
    ...options,
    headers,
    credentials: "include", // Always include for cookie support
   });
  },

  /* ---------- phone auth (for sign-up flow) ---------- */

  async ensureRecaptcha(containerId = "recaptcha-container") {
   await service.initialize();
   if (recaptchaVerifier) return recaptchaVerifier;

   const verifier = new RecaptchaVerifier(auth, containerId, {
    size: "invisible",
    callback: () => {},
    "expired-callback": () => {
     throw new Error("reCAPTCHA expired. Please try again.");
    },
   });

   await verifier.render();
   recaptchaVerifier = verifier;
   return verifier;
  },

  resetRecaptcha() {
   try {
    recaptchaVerifier?.clear();
   } catch {}
   recaptchaVerifier = null;
  },

  async sendPhoneVerification(phoneNumber) {
   await service.initialize();
   if (!phoneNumber || !phoneNumber.startsWith("+")) {
    throw new Error("Enter phone in E.164 format, e.g. +27...");
   }
   try {
    const appVerifier = await service.ensureRecaptcha();
    await appVerifier.verify();
    return await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
   } catch (err) {
    service.resetRecaptcha();
    throw createAuthError(err, PHONE_VERIFICATION_ERROR_MESSAGES);
   }
  },

  async verifyPhoneAndLinkEmail(
   confirmationResult,
   verificationCode,
   email,
   password,
   profileData,
  ) {
   try {
    const confirmResult = await confirmationResult.confirm(verificationCode);
    const phoneUser = confirmResult.user;

    const emailCred = EmailAuthProvider.credential(email, password);
    await linkWithCredential(phoneUser, emailCred);

    if (profileData) {
     await service.saveUserProfile(phoneUser, profileData);
    }

    try {
     await service.createServerSession(phoneUser);
    } catch {}

    return phoneUser;
   } catch (err) {
    throw createAuthError(err, PHONE_VERIFICATION_ERROR_MESSAGES);
   } finally {
    service.resetRecaptcha();
   }
  },

  /* ---------- phone verification (for existing users) ---------- */

  async sendPhoneVerificationToUser(
   phoneNumber,
   containerId = "phone-recaptcha-container",
  ) {
   await service.initialize();
   const user = auth?.currentUser;
   if (!user) throw new Error("Not signed in.");

   if (!phoneNumber || !phoneNumber.startsWith("+")) {
    throw new Error("Enter phone in E.164 format, e.g. +27...");
   }

   try {
    const verifier = new RecaptchaVerifier(auth, containerId, {
     size: "invisible",
     callback: () => {},
     "expired-callback": () => {
      throw new Error("reCAPTCHA expired. Please try again.");
     },
    });

    await verifier.render();
    const provider = new PhoneAuthProvider(auth);
    const verificationId = await provider.verifyPhoneNumber(
     phoneNumber,
     verifier,
    );

    // Store verifier for cleanup later
    recaptchaVerifier = verifier;

    return { verificationId };
   } catch (err) {
    service.resetRecaptcha();
    throw createAuthError(err, PHONE_VERIFICATION_ERROR_MESSAGES);
   }
  },

  async verifyPhoneForUser(verificationId, verificationCode) {
   await service.initialize();
   const user = auth?.currentUser;
   if (!user) throw new Error("Not signed in.");

   if (!verificationCode || !/^\d{6}$/.test(verificationCode)) {
    throw new Error("Enter the 6-digit code.");
   }

   if (!verificationId) {
    throw new Error("Request a code first.");
   }

   try {
    const credential = PhoneAuthProvider.credential(
     verificationId,
     verificationCode,
    );
    await updatePhoneNumber(user, credential);

    // Clean up recaptcha
    service.resetRecaptcha();

    return { success: true, phoneNumber: credential.providerId };
   } catch (err) {
    service.resetRecaptcha();
    throw createAuthError(err, PHONE_VERIFICATION_ERROR_MESSAGES);
   }
  },

  /* ---------- phone number utilities ---------- */

  validatePhoneNumber(phoneNumber) {
   if (!phoneNumber) return { valid: false, error: "Phone number required." };

   const e164Regex = /^\+\d{8,15}$/;
   const normalized = phoneNumber.replace(/\s+/g, "");

   if (!normalized.startsWith("+")) {
    return {
     valid: false,
     error: "Phone number must start with country code.",
    };
   }

   if (!e164Regex.test(normalized)) {
    return { valid: false, error: "Invalid phone number format." };
   }

   return { valid: true, normalized };
  },

  normalizePhoneNumber(phoneNumber) {
   return phoneNumber
    ? `+${phoneNumber.replace(/[^\d]/g, "")}`.replace(/^(\++)/, "+")
    : "";
  },

  /* ---------- profile ---------- */

  async saveUserProfile(_user, profileData) {
   // uid, email and phone are derived server-side from the verified session.
   // The profileSchema uses unknown(false) so sending extra fields causes a 400.
   // Strip null/undefined values — Joi rejects null for optional enum fields
   // (e.g. gender: null fails the valid() check instead of being treated as absent).
   const cleanedData = Object.fromEntries(
    Object.entries(profileData).filter(([, v]) => v !== null && v !== undefined),
   );
   const response = await service.makeAuthenticatedRequest(
    `${process.env.REACT_APP_API_URL}/user/profile`,
    {
     method: "POST",
     body: JSON.stringify(cleanedData),
    },
   );

   if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || "Failed to save user profile");
   }
   return response.json();
  },

  /* ---------- password reset ---------- */

  async passwordReset(email, continueUrl) {
   await service.initialize();
   const actionSettings = buildActionCodeSettings("resetPassword", continueUrl);
   try {
    await sendPasswordResetEmail(auth, email, actionSettings);
    return true;
   } catch (err) {
    throw createAuthError(err, FORGOT_PASSWORD_ERROR_MESSAGES);
   }
  },

  async verifyResetCode(oobCode) {
   await service.initialize();
   const code = cleanCode(oobCode);
   try {
    return await verifyPasswordResetCode(auth, code);
   } catch (err) {
    throw createAuthError(err, VERIFY_RESET_CODE_ERROR_MESSAGES);
   }
  },

  async confirmPasswordReset(oobCode, newPassword) {
   await service.initialize();
   const code = cleanCode(oobCode);
   try {
    await confirmPasswordReset(auth, code, newPassword);
    if (auth.currentUser) {
     await auth.currentUser.reload();
    }
   } catch (err) {
    throw createAuthError(err, RESET_PASSWORD_ERROR_MESSAGES);
   }
  },

  /* ---------- email verification ---------- */

  async sendEmailVerificationLink(continueUrl) {
   await service.initialize();
   const user = auth?.currentUser;
   if (!user) throw new Error("Not signed in.");

   auth.languageCode = navigator.language || "en";

   const actionSettings = buildActionCodeSettings("verifyEmail", continueUrl);
   try {
    await sendEmailVerification(user, actionSettings);
    return true;
   } catch (err) {
    throw createAuthError(err, EMAIL_VERIFICATION_ERROR_MESSAGES);
   }
  },

  async verifyEmailWithCode(oobCode) {
   await service.initialize();
   const code = cleanCode(oobCode);

   try {
    const info = await checkActionCode(auth, code);
    const emailFromCode = info?.data?.email || null;

    await applyActionCode(auth, code);

    if (auth.currentUser) {
     await auth.currentUser.reload();
     await auth.currentUser.getIdToken(true);
     await service.syncServerSession(auth.currentUser).catch(() => {});
    }

    return { email: auth.currentUser?.email || emailFromCode, verified: true };
   } catch (error) {
    throw createAuthError(error, EMAIL_VERIFICATION_ERROR_MESSAGES);
   }
  },

  async isEmailVerified() {
   await service.initialize();
   const u = await service.getCurrentUser();
   if (!u) return false;
   await u.reload();
   return !!u.emailVerified;
  },

  /* ---------- password change (in-session) ---------- */

  async changePassword(currentPassword, newPassword) {
   await service.initialize();
   const user = auth?.currentUser;
   if (!user?.email) throw new Error("Not signed in.");

   try {
    const cred = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, cred);
    await updatePassword(user, newPassword);
    return true;
   } catch (err) {
    throw createAuthError(err, SIGN_IN_ERROR_MESSAGES); // Use sign-in errors for password validation
   }
  },

  /* ---------- sign up ---------- */

  async signUp(email, password, profileData = null) {
   await service.initialize();
   try {
    const cred = await createUserWithEmailAndPassword(auth, email, password);

    // Create server session first and wait for it to fully settle
    // before making any authenticated API calls (e.g. saveUserProfile).
    // On Safari the cookie can lag behind, so we await this explicitly
    // rather than fire-and-forget.
    await service.createServerSession(cred.user);

    // Save profile in the same chain, right after the session is established.
    // This avoids the race condition where saveUserProfile is called from the
    // page before the session cookie is available in the browser.
    if (profileData) {
     try {
      await service.saveUserProfile(cred.user, profileData);
     } catch (profileErr) {
      // Non-fatal — user is created and signed in, profile can be retried
      console.error('Profile save failed after sign up:', profileErr);
     }
    }

    return cred.user;
   } catch (err) {
    throw createAuthError(err, SIGN_UP_ERROR_MESSAGES);
   }
  },

  /* ---------- account management ---------- */

  async disableAccount() {
   await service.initialize();
   const res = await service.makeAuthenticatedRequest(
    `${process.env.REACT_APP_API_URL}/user/disable`,
    {
     method: "POST",
     body: JSON.stringify({ reason: "user_initiated" }),
    },
   );

   if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err.error || "Failed to disable account");
    e.code = err.code;
    throw e;
   }
   return res.json();
  },

  async deleteAccount() {
   await service.initialize();
   const res = await service.makeAuthenticatedRequest(
    `${process.env.REACT_APP_API_URL}/user/delete`,
    {
     method: "POST",
    },
   );

   if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e = new Error(err.error || "Failed to delete account");
    e.code = err.code;
    throw e;
   }

   // Local sign-out + storage cleanup
   try {
    await service.signOut();
   } catch {}
   return res.json();
  },

  // Expose mobile detection for debugging
  isMobile() {
   return isMobile;
  },

  isSafari() {
   return isSafariBrowser;
  },

  async getUserProfile(uid) {
   const { getDatabaseAsync } = await import("configuration");
   const db = await getDatabaseAsync();
   const userDoc = await getDoc(doc(db, "users", uid));
   return userDoc.exists() ? userDoc.data() : null;
  },
 };

 return service;
};

export const authenticationService = createAuthenticationService();